import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { setTenantContext, clearTenantContext } from '../lib/supabase';
import { tenantsApi, type Tenant as TenantType, type SubscriptionPlan, type UserTenantAccess } from '../services/tenantsApi';
import { useAuth } from './AuthContext';

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  subscription_plan: SubscriptionPlan;
  settings: {
    timezone?: string;
    defaultLanguage?: string;
    branding: {
      primaryColor: string;
      logo: string;
    };
    features: {
      contests: boolean;
      rewards: boolean;
      dailyPath: boolean;
      instantWin: boolean;
      html5Games?: boolean;
      triviaQuizzes?: boolean;
    };
  };
}

export type { SubscriptionPlan };

export interface TenantRoleInfo {
  role: string;
  appUserId: string;
}

interface TenantContextType {
  tenant: Tenant | null;
  setTenant: (tenant: Tenant) => void;
  allTenants: Tenant[];
  selectedTenantId: string | null;
  setSelectedTenantId: (tenantId: string | null) => void | Promise<void>;
  isLoadingTenants: boolean;
  userTenantRoles: Map<string, TenantRoleInfo>;
  canSwitchTenants: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

const SELECTED_TENANT_KEY = 'rewardpulse_selected_tenant';

const ADMIN_ONLY_ROUTES = ['/settings', '/users', '/api-docs', '/support'];

const canAccessRoute = (role: string, route: string): boolean => {
  if (ADMIN_ONLY_ROUTES.some(r => route.startsWith(r))) {
    return role === 'admin';
  }
  return true;
};

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const { user, updateUserForTenant } = useAuth();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [userTenantRoles, setUserTenantRoles] = useState<Map<string, TenantRoleInfo>>(new Map());
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(() => {
    return localStorage.getItem(SELECTED_TENANT_KEY);
  });
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);

  const canSwitchTenants = user?.isMegaAdmin || allTenants.length > 1;

  useEffect(() => {
    const loadTenants = async () => {
      if (!user) {
        setAllTenants([]);
        setTenant(null);
        setUserTenantRoles(new Map());
        setIsLoadingTenants(false);
        return;
      }

      try {
        setIsLoadingTenants(true);

        let tenants: TenantType[] = [];
        const rolesMap = new Map<string, TenantRoleInfo>();

        if (user.isMegaAdmin) {
          tenants = await tenantsApi.fetchAllTenants();
        } else {
          const userTenantAccess = await tenantsApi.fetchUserAccessibleTenants(user.authUserId);

          tenants = userTenantAccess.map((access: UserTenantAccess) => access.tenant);

          userTenantAccess.forEach((access: UserTenantAccess) => {
            const normalizedRole = access.role === 'owner' ? 'admin' : access.role;
            rolesMap.set(access.tenant.id, {
              role: normalizedRole,
              appUserId: access.appUserId
            });
          });

          setUserTenantRoles(rolesMap);
        }

        setAllTenants(tenants);

        if (tenants.length > 0) {
          let selectedTenant: TenantType;

          const canPersistSelection = user.isMegaAdmin || tenants.length > 1;

          if (canPersistSelection) {
            const storedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
            selectedTenant = storedTenantId
              ? tenants.find(t => t.id === storedTenantId) || tenants[0]
              : tenants[0];
          } else {
            selectedTenant = tenants[0];
            localStorage.removeItem(SELECTED_TENANT_KEY);
          }

          setTenant(selectedTenant);
          setSelectedTenantIdState(selectedTenant.id);

          if (canPersistSelection) {
            localStorage.setItem(SELECTED_TENANT_KEY, selectedTenant.id);
          }

          if (!user.isMegaAdmin && rolesMap.has(selectedTenant.id)) {
            const roleInfo = rolesMap.get(selectedTenant.id)!;
            if (user.tenantId !== selectedTenant.id || user.role !== roleInfo.role) {
              updateUserForTenant({
                tenantId: selectedTenant.id,
                appUserId: roleInfo.appUserId,
                role: roleInfo.role as 'admin' | 'project_manager'
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
      } finally {
        setIsLoadingTenants(false);
      }
    };

    loadTenants();
  }, [user?.authUserId, user?.isMegaAdmin]);

  const setSelectedTenantId = useCallback(async (tenantId: string | null) => {
    if (!canSwitchTenants) {
      return;
    }

    if (!tenantId) {
      if (user?.isMegaAdmin) {
        setSelectedTenantIdState(null);
        localStorage.removeItem(SELECTED_TENANT_KEY);
        setTenant(null);
      }
      return;
    }

    let targetTenant = allTenants.find(t => t.id === tenantId);

    if (!targetTenant && user?.isMegaAdmin) {
      try {
        const fetched = await tenantsApi.fetchTenantById(tenantId);
        if (fetched) {
          targetTenant = fetched;
          setAllTenants(prev => {
            if (prev.some(t => t.id === tenantId)) return prev;
            return [...prev, fetched];
          });
        }
      } catch (err) {
        console.error('Failed to fetch tenant for impersonation:', err);
      }
    }

    if (!targetTenant) {
      return;
    }

    setSelectedTenantIdState(tenantId);
    localStorage.setItem(SELECTED_TENANT_KEY, tenantId);
    setTenant(targetTenant);

    if (!user?.isMegaAdmin && userTenantRoles.has(tenantId)) {
      const roleInfo = userTenantRoles.get(tenantId)!;
      updateUserForTenant({
        tenantId,
        appUserId: roleInfo.appUserId,
        role: roleInfo.role as 'admin' | 'project_manager'
      });

      const currentPath = window.location.pathname;
      if (!canAccessRoute(roleInfo.role, currentPath)) {
        window.location.href = '/dashboard';
      }
    }
  }, [canSwitchTenants, allTenants, user?.isMegaAdmin, userTenantRoles, updateUserForTenant]);

  useEffect(() => {
    const updateTenantContext = async () => {
      if (tenant?.id) {
        const success = await setTenantContext(tenant.id);
        if (!success) {
          console.error('Failed to set tenant context for tenant:', tenant.id);
        }
      } else {
        await clearTenantContext();
      }
    };

    updateTenantContext();
  }, [tenant?.id]);

  return (
    <TenantContext.Provider value={{
      tenant,
      setTenant,
      allTenants,
      selectedTenantId,
      setSelectedTenantId,
      isLoadingTenants,
      userTenantRoles,
      canSwitchTenants
    }}>
      {children}
    </TenantContext.Provider>
  );
};
